const REDACTED = '[REDACTED]'

/**
 * 자격증명 계열 키. 유출되면 그 자체로 계정·기기를 조작할 수 있다.
 */
const SENSITIVE_CREDENTIAL_KEY_PARTS = [
  'authorization',
  'cookie',
  'set-cookie',
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'session',
  'jwt',
  'billingKey',
  'customerUid',
]

/**
 * 개인식별정보(PII) 계열 키.
 *
 * <p>부분 문자열로 매칭하므로 `phoneNumber`, `orderAddress`, `emailAddress` 처럼 접두·접미가
 * 붙은 키도 함께 걸린다. 반대로 `name` 같은 지나치게 흔한 토큰은 넣지 않는다 — `className`,
 * `eventName`, `fileName` 까지 전부 가려져 로그가 못 쓰게 된다. 실제 사람 이름이 들어가는
 * 자리는 `fullName`/`realName`/`orderName` 처럼 한정된 형태로만 등재한다.
 */
const SENSITIVE_PII_KEY_PARTS = [
  'email',
  'phone',
  'mobile',
  'address',
  'birth',
  'fullName',
  'realName',
  'orderName',
  'userName',
  'nickname',
  'cardNumber',
]

/**
 * 키 이름이 **완전히 일치**할 때만 민감으로 판정하는 키.
 *
 * <p>부분 문자열로 매칭하면 무관한 키까지 걸리는 짧은 토큰을 여기에 둔다 — `ssn` 은 3글자라
 * `className`(cla`ssn`ame) 에도 포함된다.
 */
const SENSITIVE_EXACT_KEYS = [
  'ssn',
  'fbp',
  'fbc',
  'fbclid',
  'user_data',
  'external_id',
  'em',
  'ph',
  'userAgent',
  'sourceUrl',
  'X-Podo-Meta-Context',
]

const SENSITIVE_KEY_PARTS_LOWERCASE = [...SENSITIVE_CREDENTIAL_KEY_PARTS, ...SENSITIVE_PII_KEY_PARTS].map((key) =>
  key.toLowerCase(),
)

const SENSITIVE_EXACT_KEYS_LOWERCASE = SENSITIVE_EXACT_KEYS.map((key) => key.toLowerCase())

export const isSensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase()

  return (
    SENSITIVE_EXACT_KEYS_LOWERCASE.includes(normalizedKey) ||
    SENSITIVE_KEY_PARTS_LOWERCASE.some((sensitiveKey) => normalizedKey.includes(sensitiveKey))
  )
}

/**
 * 중첩 객체·배열 안까지 훑어 민감 키의 값을 가린다.
 *
 * <p>키 이름만 보고 판단하므로, 민감한 값이 **문자열 안에** 파묻혀 있으면(예: 객체를
 * `JSON.stringify` 해서 하나의 문자열로 넘긴 경우) 잡지 못한다. 또한 순환 참조·비용을 막기 위해
 * `MAX_DEPTH`(8) 를 넘는 서브트리는 검사하지 않고 원본 그대로 통과시키므로, 그보다 깊이 중첩된
 * 민감 키는 가려지지 않는다. 그런 값은 로그를 만드는 쪽에서 애초에 넣지 않아야 한다.
 * 이 함수는 최후 방어선이지 1차 방어선이 아니다.
 *
 * <p>가릴 것이 하나도 없으면 새 객체를 만들지 않고 **원본 참조를 그대로** 반환한다. 호출부가
 * 참조 비교로 "바뀐 값만" 다시 쓸 수 있게 하기 위함이다.
 */
export const redactValue = (value: unknown, depth = 0): unknown => {
  const MAX_DEPTH = 8

  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value
  }

  if (Array.isArray(value)) {
    const items = value.map((item) => redactValue(item, depth + 1))

    return items.some((item, index) => item !== value[index]) ? items : value
  }

  const entries = Object.entries(value as Record<string, unknown>)
  let changed = false

  const redactedEntries = entries.map(([key, entryValue]): [string, unknown] => {
    const redactedEntryValue = isSensitiveKey(key) ? REDACTED : redactValue(entryValue, depth + 1)
    changed ||= redactedEntryValue !== entryValue

    return [key, redactedEntryValue]
  })

  return changed ? Object.fromEntries(redactedEntries) : value
}

export const REDACTED_VALUE = REDACTED
